/**
 * 결제 관리 커스텀 훅
 * - React Query를 사용한 결제 데이터 페칭
 */

import { useQuery } from '@tanstack/react-query';
import { paymentsApi } from '../services/api/payments';
import type { PaymentFilters, PaymentType } from '../types/payment';
import { useState } from 'react';

function getTodayInputValue(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * 결제 목록 훅
 * 
 * @param initialPage 초기 페이지 번호
 * @param initialSize 초기 페이지 크기
 * @param paymentType 결제유형 고정값 — 지정 시 해당 유형만 조회 (온라인/오프라인 분리 페이지용)
 * @returns 결제 목록, 페이징, 통계 및 상태 관리 함수들
 */
export function usePayments(
  initialPage: number = 1,
  initialSize: number = 10,
  paymentType?: PaymentType,
) {
  const [page, setPage] = useState(initialPage);
  const [size, setSize] = useState(initialSize);
  const todayStr = getTodayInputValue();
  const [filters, setFilters] = useState<PaymentFilters>({
    paymentStatus: 'ALL',
    startDate: todayStr,
    endDate: todayStr,
    sortBy: 'dtRegist',
    sortOrder: 'DESC',
  });

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['payments', paymentType ?? 'ALL', page, size, filters],
    queryFn: () => paymentsApi.getPayments(page, size, { ...filters, paymentType }),
    // 검색/필터로 queryKey가 바뀌면 data가 잠깐 undefined가 될 수 있어 UI가 깜빡입니다.
    // 이전 데이터 유지로 상단 통계/목록이 사라졌다가 다시 생기는 현상을 방지합니다.
    placeholderData: prev => prev,
    staleTime: 30 * 1000, // 30초
    gcTime: 5 * 60 * 1000, // 5분
  });

  return {
    // 데이터
    payments: data?.content || [],
    pagination: data?.pagination,
    statistics: data?.statistics,
    
    // 상태
    isLoading,
    isFetching,
    error,
    
    // 페이징
    page,
    size,
    setPage,
    setSize,
    
    // 필터
    filters,
    setFilters,
    
    // 액션
    refetch,
  };
}



